import React from 'react'
import { connect } from 'react-redux'

import { fetchOccupancy } from '../actions/occupancy'
import { fetchMatrixs } from '../actions/matrixs'
import { fetchCustomersName } from '../actions/customers'
import { getLockersName } from '../utils/machines'
import FeatureBlockedScreen from '../components/FeatureBlockedScreen'
import LoadingProgress from '../components/LoadingProgress'

import '../styles/DashboardView.css'
import '../styles/LiveOccupancyView.css'

const POLL_INTERVAL_MS = 30000
const OFFLINE_THRESHOLD_S = 60 * 5 // mismo umbral que getMachineStatus

// Categorías del informe (colores validados sobre blanco). El orden es el de la
// barra apilada. Ocupada = RQ|RK|RM|RP (convención existente de las vistas Ocupation*).
const BUCKETS = [
  { key: 'Occupied', label: 'Ocupadas', color: '#d97706' },
  { key: 'ReservedOnline', label: 'Reservadas online', color: '#9c00d8' },
  { key: 'Free', label: 'Libres', color: '#16a34a' },
  { key: 'BleFault', label: 'Incidencia BLE', color: '#2f5fe8' },
  { key: 'OutOfOrder', label: 'Averiadas', color: '#dc2626' }
]

// LEDs como en la matriz del ERP (style.css #AV, #RK…)
const LED_GRADIENTS = {
  AV: 'linear-gradient(180deg,#86fb70,#22d51f)',
  RK: 'linear-gradient(180deg,#ffb84d,#f08a00)',
  RQ: 'linear-gradient(180deg,#ffb84d,#f08a00)',
  RP: 'linear-gradient(180deg,#ffb84d,#f08a00)',
  RM: 'linear-gradient(180deg,#ffb84d,#f08a00)',
  RA: 'linear-gradient(180deg,#ffb84d,#f08a00)',
  BL: 'linear-gradient(180deg,#d24dff,#9c00d8)',
  FB: 'linear-gradient(180deg,#86a6ff,#2f5fe8)'
}
// La matriz del ERP solo imprime el código en estos estados
const LED_SHOW_CODE = { RM: true, RQ: true, RP: true, RA: true }

const TYPE_UNITS = { S: 1, M: 2, L: 3, XL: 4, '2XL': 5, '3XL': 6 }
const MATRIX_COLUMN_UNITS = 6

function esNum(n) {
  return (n || 0).toLocaleString('es-ES')
}

function esPct(x, decimals = 1) {
  if (!isFinite(x)) x = 0
  return (100 * x).toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }) + ' %'
}

function secondsSince(dateString) {
  if (!dateString) return null
  const t = Date.parse(dateString)
  if (isNaN(t)) return null
  return Math.max(0, Math.round((Date.now() - t) / 1000))
}

function zoneLabel(zone, zoneName) {
  if (zoneName) return zoneName
  if (zone === 0) return 'Sin zona'
  return `Zona ${zone}`
}

class LiveOccupancyView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      autoRefresh: true,
      secondsAgo: 0,
      zone: '',
      zoneOptions: [],
      customerId: '',
      customerInput: '',
      detailSerial: null
    }

    this.pollTimer = null
    this.tickTimer = null

    this.load = this.load.bind(this)
    this.toggleAutoRefresh = this.toggleAutoRefresh.bind(this)
    this.handleZoneChange = this.handleZoneChange.bind(this)
    this.handleCustomerInput = this.handleCustomerInput.bind(this)
  }

  componentDidMount() {
    this.load()

    if (this.props.role === 'ADMIN') {
      this.props.fetchCustomersName()
    }

    this.pollTimer = setInterval(() => {
      if (this.state.autoRefresh && !document.hidden && !this.props.loading) {
        this.load()
      }
    }, POLL_INTERVAL_MS)

    this.tickTimer = setInterval(() => {
      this.setState(prev => ({ secondsAgo: prev.secondsAgo + 1 }))
    }, 1000)
  }

  componentWillUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.tickTimer) clearInterval(this.tickTimer)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data && this.props.data) {
      const updates = { secondsAgo: 0 }
      // Las opciones del filtro de zona solo se refrescan con respuestas sin filtro,
      // para no perder la lista al filtrar.
      if (this.state.zone === '' && Array.isArray(this.props.data.ByZone)) {
        updates.zoneOptions = this.props.data.ByZone
      }
      this.setState(updates)
    }
  }

  load() {
    const { role } = this.props
    const customer = role === 'ADMIN' && this.state.customerId !== '' ? this.state.customerId : undefined
    const zone = this.state.zone !== '' ? this.state.zone : undefined
    this.props.fetchOccupancy(customer, zone)
  }

  toggleAutoRefresh() {
    this.setState(prev => ({ autoRefresh: !prev.autoRefresh }))
  }

  handleZoneChange(event) {
    this.setState({ zone: event.target.value, detailSerial: null }, this.load)
  }

  handleCustomerInput(event) {
    const value = event.target.value
    const customers = this.props.customers || []
    const match = customers.find(c => c.Fullname === value)

    this.setState({
      customerInput: value,
      customerId: match ? match._id : '',
      zone: '',
      zoneOptions: [],
      detailSerial: null
    }, () => {
      if (match || value === '') {
        this.load()
      }
    })
  }

  toggleDetail(row) {
    const next = this.state.detailSerial === row.Serial ? null : row.Serial
    this.setState({ detailSerial: next })

    if (next) {
      const matrixs = this.props.matrixs || []
      const loaded = matrixs.find(m => m.Serial === row.Serial)
      if (!loaded) {
        this.props.fetchMatrixs(row.MachineId)
      }
    }
  }

  renderKpis(totals) {
    const cards = [
      { css: 'occupancy', icon: 'fa-percentage', label: 'Ocupación', value: esPct(totals.Occupancy) },
      { css: 'total', icon: 'fa-th-large', label: 'Taquillas', value: esNum(totals.Configured) },
      { css: 'broken', icon: 'fa-lock', label: 'Ocupadas', value: esNum(totals.Occupied) },
      { css: 'green-block', icon: 'fa-lock-open', label: 'Libres', value: esNum(totals.Free) },
      { css: 'pinpad-block', icon: 'fa-qrcode', label: 'Reserva online', value: esNum(totals.ReservedOnline) },
      { css: 'red-block', icon: 'fa-tools', label: 'Fuera de servicio', value: esNum(totals.OutOfOrder) }
    ]

    return (
      <div className="dashboard-kpi-wrapper mb-3">
        <div className="dashboard-kpi-row">
          {cards.map(card => (
            <div key={card.label} className={`dashboard-kpi-card ${card.css}`}>
              <div className="kpi-icon"><i className={`fas ${card.icon}`}></i></div>
              <div className="kpi-content">
                <div className="kpi-label">{card.label}</div>
                <p className="kpi-value">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  renderFillbar(row, small) {
    const conf = row.Configured || 0
    if (conf <= 0) {
      return <div className={`lov-fillbar${small ? ' lov-small' : ''}`}></div>
    }

    return (
      <div className={`lov-fillbar${small ? ' lov-small' : ''}`}>
        {BUCKETS.map(bucket => {
          const value = row[bucket.key] || 0
          if (value <= 0) return null
          const pct = 100 * value / conf
          const label = !small && pct >= 8 ? esNum(value) : ''
          return (
            <div
              key={bucket.key}
              className="lov-seg"
              style={{ width: `${pct.toFixed(3)}%`, background: bucket.color }}
              title={`${bucket.label}: ${esNum(value)} (${esPct(value / conf)})`}
            >
              {label}
            </div>
          )
        })}
      </div>
    )
  }

  renderPark(totals) {
    return (
      <div className="dashboard-card-section lov-section">
        <div className="card-header-custom">
          <span><i className="fas fa-water mr-2"></i>Llenado del parque</span>
          <span className="lov-section-sub">composición en este instante</span>
        </div>
        <div className="card-body-custom">
          <div className="lov-hero-row">
            <span className="lov-hero-num">{esPct(totals.Occupancy)}</span>
            <span className="lov-hero-cap">
              {esNum(totals.Occupied)} taquillas ocupadas de {esNum(totals.Sellable)} vendibles ({esNum(totals.Configured)} configuradas)
            </span>
          </div>
          {this.renderFillbar(totals, false)}
          <div className="lov-legend">
            {BUCKETS.map(bucket => (
              <span key={bucket.key} className="lov-lg">
                <i style={{ background: bucket.color }}></i>
                {bucket.label} <b>{esNum(totals[bucket.key])}</b>
                {totals.Configured > 0 && <span>· {esPct((totals[bucket.key] || 0) / totals.Configured)}</span>}
              </span>
            ))}
          </div>
          {totals.NotConfigured > 0 && (
            <div className="lov-nc-note">
              <i></i>
              <span>{esNum(totals.NotConfigured)} puertas no configuradas — excluidas de la barra y del cálculo de ocupación.</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  renderZones(byZone, multiCustomer) {
    if (!byZone || byZone.length === 0) return null

    return (
      <div className="dashboard-card-section lov-section">
        <div className="card-header-custom">
          <span><i className="fas fa-map-marked-alt mr-2"></i>Por zonas</span>
          <span className="lov-section-sub">ordenadas por ocupación</span>
        </div>
        <div className="card-body-custom">
          <div className="lov-zone-grid">
            {byZone.map(zone => {
              const dotColor = zone.ZoneColor && zone.ZoneColor !== '#000536' ? zone.ZoneColor : '#94a3b8'
              return (
                <div key={`${zone.CustomerId || ''}:${zone.Zone}`} className="lov-zone-card">
                  <div className="lov-zone-head">
                    <span className="lov-zone-dot" style={{ background: dotColor }}></span>
                    <span className="lov-zone-name">{zoneLabel(zone.Zone, zone.ZoneName)}</span>
                    <span className="lov-zone-pct">{esPct(zone.Occupancy, 0)}</span>
                  </div>
                  <div className="lov-zone-sub">
                    {zone.Machines} máquina{zone.Machines === 1 ? '' : 's'} · {esNum(zone.Configured)} taquillas
                    {multiCustomer && zone.CustomerName ? ` · ${zone.CustomerName}` : ''}
                  </div>
                  {this.renderFillbar(zone, true)}
                  <div className="lov-zone-counts">
                    <span>Ocup. <b>{esNum(zone.Occupied)}</b></span>
                    <span>Libres <b>{esNum(zone.Free)}</b></span>
                    <span>Reserv. <b>{esNum(zone.ReservedOnline)}</b></span>
                    <span>F. servicio <b>{esNum(zone.OutOfOrder)}</b></span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  renderTypes(byType) {
    const entries = Object.keys(byType || {})
      .map(key => ({ key, ...byType[key] }))
      .filter(entry => entry.Configured > 0)
      .sort((a, b) => b.Configured - a.Configured)

    if (entries.length === 0) return null

    return (
      <div className="dashboard-card-section lov-section">
        <div className="card-header-custom">
          <span><i className="fas fa-ruler-vertical mr-2"></i>Por talla</span>
          <span className="lov-section-sub">ocupadas ÷ configuradas</span>
        </div>
        <div className="card-body-custom">
          {entries.map(entry => {
            const p = entry.Occupied / entry.Configured
            const fill = p >= 0.95 ? '#dc2626' : p >= 0.80 ? '#d97706' : '#0077e6'
            const track = p >= 0.95 ? '#f8d7d7' : p >= 0.80 ? '#f6e3c6' : '#d5e8fb'
            return (
              <div key={entry.key} className="lov-meter-row">
                <div className="lov-meter-lab">
                  {getLockersName(entry.key)}
                  <small>{esNum(entry.Occupied)} / {esNum(entry.Configured)}</small>
                </div>
                <div
                  className="lov-meter"
                  style={{ background: track }}
                  title={`${getLockersName(entry.key)}: ${esNum(entry.Occupied)} ocupadas de ${esNum(entry.Configured)}`}
                >
                  <i style={{ width: `${(100 * p).toFixed(1)}%`, background: fill }}></i>
                </div>
                <div className="lov-meter-val">
                  {entry.Free === 0 && <span className="lov-soldout">AGOTADA</span>}
                  {esPct(p, 0)}
                </div>
              </div>
            )
          })}
          <div className="lov-meter-note">Color del relleno: azul &lt; 80 % · ámbar 80–94 % · rojo ≥ 95 %</div>
        </div>
      </div>
    )
  }

  renderMatrixDetail(row) {
    const matrixs = this.props.matrixs || []
    const matrix = matrixs.find(m => m.Serial === row.Serial)

    if (!matrix) {
      return <div className="lov-matrix-loading">Cargando matriz…</div>
    }

    // Mismo recorte que el resumen del servidor: Physical = puertas reales
    const lockers = (matrix.Locker || []).slice(0, row.Physical || undefined)
    const firstIndex = row.FirstIndex || 1

    // Empaquetado en columnas de 6 unidades de alto (S=1 … 3XL=6), como MatrixConstructor
    const columns = []
    let current = { units: 0, doors: [] }
    lockers.forEach((locker, index) => {
      const units = TYPE_UNITS[locker.Type] || 1
      if (current.units + units > MATRIX_COLUMN_UNITS && current.doors.length > 0) {
        columns.push(current)
        current = { units: 0, doors: [] }
      }
      current.doors.push({ locker, number: firstIndex + index, units })
      current.units += units
    })
    if (current.doors.length > 0) columns.push(current)

    return (
      <div>
        <div className="lov-matrix-scroll">
          <div className="lov-matrix">
            {columns.map((column, colIdx) => (
              <div key={colIdx} className="lov-mcol">
                {column.doors.map(door => {
                  const status = door.locker.Configured ? (door.locker.Status || '').toUpperCase() : null
                  const height = 26 * door.units + 5 * (door.units - 1)
                  const gradient = status ? LED_GRADIENTS[status] : null
                  return (
                    <div
                      key={door.number}
                      className={`lov-door${status === null ? ' lov-nc' : ''}`}
                      style={{ height: `${height}px` }}
                      title={`Puerta ${door.number} · ${status === null ? 'no configurada' : status}`}
                    >
                      <span className="lov-dnum">{door.number}</span>
                      {status !== null && status !== 'FL' && (
                        <span className="lov-led" style={{ background: gradient || 'linear-gradient(180deg,#9aa4b1,#5b6878)' }}>
                          {LED_SHOW_CODE[status] ? status : ''}
                        </span>
                      )}
                      {status === 'FL' && <span className="lov-fl-x">✕</span>}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="lov-matrix-legend">
          <span><span className="lov-led" style={{ background: LED_GRADIENTS.AV }}></span> Libre</span>
          <span><span className="lov-led" style={{ background: LED_GRADIENTS.RK }}></span> Ocupada</span>
          <span><span className="lov-led" style={{ background: LED_GRADIENTS.BL }}></span> Reservada online</span>
          <span><span className="lov-led" style={{ background: LED_GRADIENTS.FB }}></span> Incidencia BLE</span>
          <span><span className="lov-fl-x" style={{ position: 'static' }}>✕</span> Avería</span>
          <span><span className="lov-led" style={{ background: 'linear-gradient(180deg,#3c3c3c,#101010)' }}></span> No configurada</span>
        </div>
      </div>
    )
  }

  renderMachines(machines, multiCustomer) {
    if (!machines || machines.length === 0) return null

    const sorted = machines.slice().sort((a, b) => b.Occupancy - a.Occupancy)

    return (
      <div className="dashboard-card-section lov-section">
        <div className="card-header-custom">
          <span><i className="fas fa-server mr-2"></i>Por máquina</span>
          <span className="lov-section-sub">clic en una fila para ver su matriz</span>
        </div>
        <div className="card-body-custom">
          <div className="lov-table-wrap">
            <table className="lov-table">
              <thead>
                <tr>
                  <th>Máquina</th>
                  <th>Zona</th>
                  <th style={{ minWidth: '150px' }}>Composición</th>
                  <th style={{ textAlign: 'right' }}>Ocupación</th>
                  <th style={{ textAlign: 'right' }}>Libres</th>
                  <th>Señal</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(row => {
                  const seconds = secondsSince(row.LastSignal)
                  const offline = seconds === null || seconds >= OFFLINE_THRESHOLD_S
                  const open = this.state.detailSerial === row.Serial
                  return (
                    <React.Fragment key={row.Serial}>
                      <tr
                        className={`lov-mrow${offline ? ' lov-mrow-off' : ''}`}
                        onClick={() => this.toggleDetail(row)}
                      >
                        <td>
                          <b>{row.Serial}</b>
                          {row.Location ? <div className="lov-mloc">{row.Location}</div> : null}
                          {multiCustomer && row.CustomerName ? <div className="lov-mloc">{row.CustomerName}</div> : null}
                        </td>
                        <td>{zoneLabel(row.Zone, row.ZoneName)}</td>
                        <td>{this.renderFillbar(row, true)}</td>
                        <td className="lov-num"><b>{esPct(row.Occupancy, 0)}</b></td>
                        <td className="lov-num">{esNum(row.Free)}</td>
                        <td>
                          {offline
                            ? <span className="lov-sig-bad">⚠ sin señal{seconds !== null ? ` · ${Math.round(seconds / 60)} min` : ''}</span>
                            : <span className="lov-sig-ok">hace {seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`}</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan="6" className="lov-matrix-cell">
                            {this.renderMatrixDetail(row)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { role, customerType, data, loading, error, customers } = this.props

    // De momento la pantalla es solo para ADMIN; cualquier cliente que llegue
    // por URL directa ve la pantalla de funcionalidad no activada.
    if (role === 'CUSTOMER') {
      return <FeatureBlockedScreen featureName="live_occupancy" featureLabel="Ocupación en vivo" />
    }

    if (!data && loading) {
      return <LoadingProgress />
    }

    const totals = data ? data.Totals : null
    const secondsAgo = this.state.secondsAgo
    const agoLabel = secondsAgo < 120 ? `${secondsAgo} s` : `${Math.round(secondsAgo / 60)} min`
    const customerIds = data ? [...new Set((data.Machines || []).map(m => m.CustomerId).filter(Boolean))] : []
    const multiCustomer = customerIds.length > 1

    return (
      <div className="dashboard-view p-3">
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h2 className="dashboard-title"><i className="fas fa-door-open"></i>Ocupación en vivo</h2>
            <span className="lov-live-chip">
              <span className={`lov-live-dot${this.state.autoRefresh ? '' : ' paused'}`}></span>
              Actualizado hace {agoLabel} · auto 30 s
              <button type="button" onClick={this.toggleAutoRefresh}>
                {this.state.autoRefresh ? 'Pausar' : 'Reanudar'}
              </button>
            </span>
          </div>
          <div className="dashboard-actions">
            {role === 'ADMIN' && (
              <React.Fragment>
                <input
                  className="dashboard-filter-input"
                  type="text"
                  list="lov-customers"
                  placeholder="Cliente (todos)"
                  value={this.state.customerInput}
                  onChange={this.handleCustomerInput}
                  style={{ minWidth: '190px' }}
                />
                <datalist id="lov-customers">
                  {(customers || []).map(customer => (
                    <option key={customer._id} value={customer.Fullname} />
                  ))}
                </datalist>
              </React.Fragment>
            )}
            <select
              className="dashboard-filter-input"
              value={this.state.zone}
              onChange={this.handleZoneChange}
            >
              <option value="">Todas las zonas</option>
              {this.state.zoneOptions.map(zone => (
                <option key={`${zone.CustomerId || ''}:${zone.Zone}`} value={zone.Zone}>
                  {zoneLabel(zone.Zone, zone.ZoneName)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={this.load}
              disabled={loading}
              title="Actualizar ahora"
            >
              <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {error && (
          <div className="lov-error">
            <i className="fas fa-exclamation-circle mr-1"></i>
            No se pudo actualizar la ocupación{error.message ? `: ${error.message}` : '.'} Se reintentará automáticamente.
          </div>
        )}

        {data && (
          <div className={loading ? 'lov-refreshing' : ''}>
            {(data.Machines || []).length === 0 ? (
              <div className="dashboard-card-section lov-section">
                <div className="card-body-custom">
                  No hay taquillas en el alcance seleccionado.
                </div>
              </div>
            ) : (
              <React.Fragment>
                {this.renderKpis(totals)}
                {this.renderPark(totals)}
                {this.renderZones(data.ByZone, multiCustomer)}
                {this.renderTypes(data.ByType)}
                {this.renderMachines(data.Machines, multiCustomer)}
              </React.Fragment>
            )}
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  role: state.auth.role,
  customerType: state.auth.type,
  data: state.occupancy.data,
  loading: state.occupancy.loading,
  error: state.occupancy.error,
  matrixs: state.matrixs.matrixs,
  customers: state.customers.customers
})

const mapDispatchToProps = dispatch => ({
  fetchOccupancy: (customer, zone) => dispatch(fetchOccupancy(customer, zone)),
  fetchMatrixs: machine => dispatch(fetchMatrixs(machine, undefined, undefined, undefined)),
  fetchCustomersName: () => dispatch(fetchCustomersName())
})

export default connect(mapStateToProps, mapDispatchToProps)(LiveOccupancyView)
